import { registrationAttribution } from '../utils/registrationAttribution';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';
import {
  getCurrentWorkspaceId,
  getStoredStudioToken,
  setCurrentWorkspaceId,
  setStoredStudioToken,
  studioRequest
} from '../api/studioClient';
import {
  DEFAULT_FEATURES,
  DEFAULT_LIMITS,
  Entitlement,
  StudioUser,
  Workspace,
  WorkspaceMembership
} from '../models/studio';

type User = StudioUser & {
  phone?: string;
  address?: string;
  state?: string;
  country?: string;
  role?: string;
  billingAddress?: Record<string, any>;
  reportBranding?: Record<string, any>;
  createdAt?: string | Date;
  studioTrial?: {
    days?: number;
    startedAt?: string | Date;
    endsAt?: string | Date;
    isActive?: boolean;
    remainingMs?: number;
  };
  subscription: Record<string, any>;
  isPremium?: boolean;
  canCreateChart?: boolean;
  federation?: {
    isShadow?: boolean;
    remoteProduct?: 'kundlistar' | 'studio';
    status?: 'active' | 'revoked' | 'conflict';
  };
  preferences?: Record<string, any> & {
    language?: string;
    chartStyle?: string;
    timezone?: string;
    notifications?: Record<string, boolean>;
  };
};

type AuthState = {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  workspaces: Workspace[];
  memberships: WorkspaceMembership[];
  currentWorkspaceId: string | null;
};

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string; workspaces: Workspace[]; memberships: WorkspaceMembership[]; currentWorkspaceId: string | null } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SWITCH_WORKSPACE'; payload: string }
  | { type: 'CLEAR_ERROR' };

type AuthContextType = {
  state: AuthState;
  currentWorkspace: Workspace | null;
  currentMembership: WorkspaceMembership | null;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<User | null>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  clearError: () => void;
};

const normalizeWorkspace = (workspace: any): Workspace => ({
  id: String(workspace?.id || workspace?._id || workspace?.workspaceId || 'personal'),
  name: String(workspace?.name || 'Personal Workspace'),
  type: 'PERSONAL',
  plan: workspace?.plan,
  entitlement: {
    status: workspace?.entitlement?.status || 'ACTIVE',
    features: { ...DEFAULT_FEATURES, ...(workspace?.entitlement?.features || {}) },
    limits: { ...DEFAULT_LIMITS, ...(workspace?.entitlement?.limits || {}) },
    usage: workspace?.entitlement?.usage || workspace?.usage || {},
  } as Entitlement,
});

const normalizeWorkspaceRole = (role?: string) => (
  String(role || '').toUpperCase() === 'ADMIN' ? 'ADMIN' : 'OWNER'
);

const normalizeSession = (payload: any, token: string) => {
  const user = (payload?.user || payload) as User;
  const memberships = (payload?.memberships || user?.memberships || []).map((membership: any) => {
    const workspace = normalizeWorkspace(membership.workspace || membership);
    return {
      id: String(membership.id || membership._id || `${workspace.id}:${normalizeWorkspaceRole(membership.role)}`),
      workspaceId: workspace.id,
      role: normalizeWorkspaceRole(membership.role),
      workspace,
    } as WorkspaceMembership;
  });

  const explicitWorkspaces = (payload?.workspaces || []).map(normalizeWorkspace);
  const membershipWorkspaces = memberships.map((membership: WorkspaceMembership) => membership.workspace).filter(Boolean) as Workspace[];
  const workspaces = explicitWorkspaces.length ? explicitWorkspaces : membershipWorkspaces;
  const fallbackWorkspace = workspaces[0] || normalizeWorkspace({ id: 'personal', name: 'Personal Workspace', type: 'PERSONAL' });
  const currentWorkspaceId = payload?.currentWorkspaceId || user?.currentWorkspaceId || getCurrentWorkspaceId() || fallbackWorkspace.id;
  const resolvedMemberships = memberships.length ? memberships : [{
    id: `${fallbackWorkspace.id}:OWNER`,
    workspaceId: fallbackWorkspace.id,
    role: 'OWNER',
    workspace: fallbackWorkspace,
  } as WorkspaceMembership];

  return {
    user: {
      ...user,
      currentWorkspaceId,
      subscription: user.subscription || { type: 'Free Studio', isActive: true, chartsRemaining: null },
    },
    token,
    workspaces: workspaces.length ? workspaces : [fallbackWorkspace],
    memberships: resolvedMemberships,
    currentWorkspaceId,
  };
};

const initialState: AuthState = {
  user: null,
  token: getStoredStudioToken(),
  isLoading: !!getStoredStudioToken(),
  error: null,
  isAuthenticated: false,
  workspaces: [],
  memberships: [],
  currentWorkspaceId: getCurrentWorkspaceId(),
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        ...action.payload,
        isLoading: false,
        isAuthenticated: true,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
        isAuthenticated: false,
        error: action.payload,
        workspaces: [],
        memberships: [],
        currentWorkspaceId: null,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        token: null,
        isLoading: false,
        currentWorkspaceId: null,
      };
    case 'UPDATE_USER':
      return { ...state, user: action.payload };
    case 'SWITCH_WORKSPACE':
      return {
        ...state,
        currentWorkspaceId: action.payload,
        user: state.user ? { ...state.user, currentWorkspaceId: action.payload } : state.user,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const applySession = (payload: any, token: string) => {
    const session = normalizeSession(payload, token);
    setStoredStudioToken(token);
    setCurrentWorkspaceId(session.currentWorkspaceId);
    dispatch({ type: 'AUTH_SUCCESS', payload: session });
    window.dispatchEvent(new CustomEvent('kundlistar-profile-changed', { detail: session.user }));
    return session.user;
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = getStoredStudioToken();
      if (!token) {
        dispatch({ type: 'AUTH_FAILURE', payload: '' });
        return;
      }

      try {
        dispatch({ type: 'AUTH_START' });
        setStoredStudioToken(token);
        const payload = await studioRequest<any>({ url: '/auth/session', method: 'GET' }, 'Unable to verify session');
        applySession(payload, token);
      } catch (error: any) {
        if (error.status === 401 || error.status === 403) {
          setStoredStudioToken(null);
          window.dispatchEvent(new CustomEvent('kundlistar-profile-changed', { detail: null }));
          setCurrentWorkspaceId(null);
          dispatch({ type: 'AUTH_FAILURE', payload: 'Session expired. Please sign in again.' });
          return;
        }
        dispatch({ type: 'AUTH_FAILURE', payload: error.message || 'Unable to verify session. Please check your connection.' });
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const payload = await studioRequest<any>({
        url: '/auth/login',
        method: 'POST',
        data: { email, password },
      }, 'Login failed');
      applySession(payload, payload.token || payload.accessToken);
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message || 'Login failed' });
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const payload = await studioRequest<any>({
        url: '/auth/register',
        method: 'POST',
        data: {
          name,
          email,
          password,
          persona: 'individual',
          registration: registrationAttribution(),
          preferences: {
            language: 'English',
            chartStyle: 'North',
            timezone: 'Asia/Kolkata',
            nodeMode: 'true',
          },
        },
      }, 'Registration failed');
      applySession(payload, payload.token || payload.accessToken);
    } catch (error: any) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message || 'Registration failed' });
      throw error;
    }
  };

  const logout = () => {
    setStoredStudioToken(null);
    setCurrentWorkspaceId(null);
    dispatch({ type: 'LOGOUT' });
    window.dispatchEvent(new CustomEvent('kundlistar-profile-changed', { detail: null }));
  };

  const updateUser = async (userData: Partial<User>) => {
    const user = await studioRequest<User>({
      url: '/me',
      method: 'PATCH',
      data: userData,
    }, 'Update failed');
    dispatch({ type: 'UPDATE_USER', payload: user });
    window.dispatchEvent(new CustomEvent('kundlistar-profile-changed', { detail: user }));
  };

  const refreshUser = async () => {
    if (!state.token) return null;
    const payload = await studioRequest<any>({ url: '/auth/session', method: 'GET' }, 'Refresh failed');
    return applySession(payload, state.token);
  };

  const switchWorkspace = async (workspaceId: string) => {
    setCurrentWorkspaceId(workspaceId);
    dispatch({ type: 'SWITCH_WORKSPACE', payload: workspaceId });
    await studioRequest({ url: `/workspaces/${workspaceId}/switch`, method: 'POST' }, 'Workspace switch failed').catch(() => undefined);
  };

  const currentWorkspace = useMemo(() => (
    state.workspaces.find((workspace) => workspace.id === state.currentWorkspaceId) || state.workspaces[0] || null
  ), [state.currentWorkspaceId, state.workspaces]);

  const currentMembership = useMemo(() => (
    state.memberships.find((membership) => membership.workspaceId === currentWorkspace?.id) || state.memberships[0] || null
  ), [currentWorkspace?.id, state.memberships]);

  const contextValue: AuthContextType = {
    state,
    currentWorkspace,
    currentMembership,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    switchWorkspace,
    clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
